import { useMemo } from 'react';
import useProductCustomFieldDefinitions from '../queries/product/useProductCustomFieldDefinitions';
import useProductCustomFields from '../queries/product/useProductCustomFields';
import { CustomAttribute } from '../../types/product/customAttribute';
import { ProductCustomFieldsResponseDto } from '../../types/api/product-custom-fields';

interface UseProductCustomAttributesArgs {
  productId: number | null;
}

// ProductCustomFieldsResponseDto에서 커스텀 필드만 추출 (메타데이터 제외)
type CustomFieldValues = Omit<ProductCustomFieldsResponseDto, 'productId' | 'createdAt' | 'updatedAt'>;

/**
 * 상품의 커스텀 속성 데이터를 조회하고 병합하는 훅
 * - definitions (정의)와 values (값)를 합쳐서 CustomAttribute 배열 생성
 * - productId가 null이면 빈 배열 반환
 */
export const useProductCustomAttributes = ({ productId }: UseProductCustomAttributesArgs): CustomAttribute[] => {
  // 커스텀 필드 정의 조회
  const { data: customFieldDefinitions } = useProductCustomFieldDefinitions();

  // 상품별 커스텀 필드 값 조회
  const { data: customFieldValues } = useProductCustomFields({
    productId: productId ?? 0,
    options: {
      enabled: !!productId,
    },
  });

  // definitions와 values를 합쳐서 CustomAttribute 배열 생성
  const customAttributes = useMemo<CustomAttribute[]>(() => {
    if (!customFieldDefinitions || !customFieldValues || !productId) {
      return [];
    }

    return customFieldDefinitions.fields.map((definition) => ({
      ...definition,
      value: customFieldValues[definition.key as keyof CustomFieldValues] ?? null,
    }));
  }, [customFieldDefinitions, customFieldValues, productId]);

  return customAttributes;
};

export default useProductCustomAttributes;
